import { defineStore } from 'pinia';
import TranslationService from '@/services/translation';

interface TranslationState {
  /** Translation cache keyed by `${trimmedOriginalText}:${lowercasedLanguage}` -> translated text */
  cache: Record<string, string>;
  /** True while a translate() call is in flight */
  isTranslating: boolean;
}

// Simple translation caching mechanism.
// We get translation based on Text. We do not assign it to Bot, User or Message.
// Reason => We simply need to translate text. If it matches and we already have it, there is NO reason to trigger translation process again
// What improves performance and reduces costs. We also may need to add Text Caching mechanism in API side to reduce costs and improve performance even more. But it is not in scope for now.
const prepareTranslationText = (t: any): string => (!!t ? String(t) : '').trim();

/**
 * Pinia conversion of legacy vuex module `src/store/modules/translation.ts` (namespace
 * `translation`). In-memory only — no `vuexPersist` option (translation cache is not persisted
 * across sessions in the legacy module either).
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy had BOTH a state prop `isTranslating` and an identity-passthrough getter
 *   `isTranslating(state) => state.isTranslating`. The getter is renamed `isTranslatingGetter`;
 *   consumers should prefer reading `store.isTranslating` directly off state.
 * - `clearTranslations` existed as BOTH an action (which just committed the mutation of the same
 *   name) and a mutation of the same name — merged into a single action.
 * - `setIsTranslating` and `setTranslation` only existed as mutations (no matching actions) —
 *   become same-named actions.
 */
export const useTranslationStore = defineStore('translation', {
  state: (): TranslationState => ({
    cache: {},
    isTranslating: false,
  }),
  getters: {
    getTranslation:
      (state) =>
      ({ text, language = 'en' }: { text: string; language?: string }) => {
        const lang = language?.toLocaleLowerCase() || 'en';
        const key = prepareTranslationText(text);
        return state.cache[`${key}:${lang}`] || null;
      },
    /**
     * Renamed from legacy getter `isTranslating` (collides with the state prop of the same
     * name — the state prop keeps the name). Identity passthrough; consumers can also just read
     * `store.isTranslating` directly.
     */
    isTranslatingGetter: (state) => state.isTranslating,
  },
  actions: {
    /**
     * Merged from legacy action `clearTranslations` (which only committed the mutation of the
     * same name) + legacy mutation `clearTranslations`.
     */
    clearTranslations() {
      this.cache = {};
    },
    /**
     * Converted from legacy mutation `setIsTranslating` (no matching legacy action existed).
     */
    setIsTranslating(val: boolean) {
      this.isTranslating = val;
    },
    /**
     * Converted from legacy mutation `setTranslation` (no matching legacy action existed).
     */
    setTranslation({ language = 'en', original, translated }: { language?: string; original: string; translated: string }) {
      const lang = language?.toLocaleLowerCase() || 'en';
      const key = prepareTranslationText(original);
      // We can use hash of original text as key to reduce cache size, but for simplicity we will use original text as key for now
      this.cache[`${key}:${lang}`] = translated;
    },

    async translate({ botId, language = 'en', texts = [], useCache = true }: { botId?: string; language?: string; texts?: string[] | string; useCache?: boolean } = {}) {
      // Incoming array
      const formatTranslated = (incomingArray: string[], translationsMap: Record<string, string>) => {
        return incomingArray.map((text) => ({
          original: text,
          translated: translationsMap[prepareTranslationText(text)] || null, // Return null if No translation available, to pass this on next try
        }));
      };

      // Find all possible in cache and translate Rest | Get only unique texts to translate
      if (!texts || !texts.length) return { success: true, data: [] };
      if (typeof texts === 'string') texts = [texts];
      if (Array.isArray(texts) && !texts.length) return { success: true, data: [] };

      // Declared outside try so the catch block can reference it (was scoped to the try in the JS version)
      // We know that empty string should be translated to empty string, so we can add it to translations right away
      const translationMap: Record<string, string> = { '' : '' };

      try {
        this.setIsTranslating(true);

        const uniqueTexts = new Set(texts.map(prepareTranslationText));

        // If has empty string => remove it and add to translations with empty string as translation, because it is the only correct translation for empty string
        if (uniqueTexts.has('')) {
          uniqueTexts.delete('');
        }

        if (useCache) {
          Array.from(uniqueTexts).forEach((text) => {
            const cached = this.getTranslation({ text, language });
            // Remove from unique texts to translate
            if (cached) {
              translationMap[text] = cached;
              uniqueTexts.delete(text);
            }
          });
        }

        const textsToTranslate = Array.from(uniqueTexts);
        let translationArray: any[] = [];

        if (textsToTranslate.length) {
          const res: any = await TranslationService.getTranslations(botId!, textsToTranslate, language) || [];
          translationArray = res.translation || [];
        }

        // We can only be sure that translation is correct if API returns the same number of translations as requested.
        if (textsToTranslate?.length === translationArray?.length) {
          // Array doesn't have original, that's why we need to map it back to original texts based on index
          translationArray.forEach((t, index) => {
            const original = textsToTranslate[index];
            if (!t?.text) {
              console.warn('Translation API returned empty translation for text', { text: original, language });
              return;
            }

            translationMap[original] = t.text;
            if (useCache) this.setTranslation({ language, original, translated: t.text });
          });
        } else {
          console.warn('Translation API returned different number of translations than requested', { requested: textsToTranslate.length, returned: translationArray?.length });
        }

        // Format translation as [{ original, translated }]
        // null where we're not 100% sure about translation correctness

        return { success: true, data: formatTranslated(texts, translationMap) };
      } catch (e: any) {
        console.error('Translation failed', e);
        const result: any = { success: false, error: e.message || 'Translation failed' };
        if (useCache && Object.keys(translationMap).length > 1) {
          // At least return cached translations if we have them, otherwise return only error
          result.data = formatTranslated(texts, translationMap);
        }

        return result;
      } finally {
        this.setIsTranslating(false);
      }
    },
  },
});
